import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getUserEmail, userHasGroup, userHasAnyRoleOrGroup, isAuthenticated } from "../utils/auth";
import { logoutUser } from "../api/users";
import { useBrand, useBrandColors } from "../brand/BrandContext";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import MenuIcon from "@mui/icons-material/Menu";
import InfoIcon from "@mui/icons-material/Info";
import PhoneIcon from "@mui/icons-material/Phone";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CloseIcon from "@mui/icons-material/Close";
import { scrollLandingToTop } from "../utils/ScrollToTop";

const GAP_BELOW_HEADER = 8;

const Header = () => {
  const { t, i18n } = useTranslation("common");
  const theme = useTheme();
  const brand = useBrand();
  const BRAND = useBrandColors();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const email = getUserEmail();
  const headerRef = useRef(null);

  const MAIN_NAV = [
    { to: "/about", labelKey: "header.about", icon: <InfoIcon /> },
    { to: "/contact", labelKey: "header.contacts", icon: <PhoneIcon /> },
  ];

  useEffect(() => {
    const updateHeight = () => {
      const h = headerRef.current?.offsetHeight;
      if (typeof h === "number" && h > 0) {
        document.documentElement.style.setProperty("--header-height", `${h}px`);
        document.documentElement.style.setProperty("--sticky-search-top", `${h + GAP_BELOW_HEADER}px`);
      }
    };
    const timer = setTimeout(updateHeight, 150);
    const ro = new ResizeObserver(updateHeight);
    const attach = () => {
      if (headerRef.current) {
        ro.observe(headerRef.current);
        updateHeight();
      }
    };
    const attachTimer = setTimeout(attach, 200);
    return () => {
      clearTimeout(timer);
      clearTimeout(attachTimer);
      ro.disconnect();
    };
  }, []);

  const [accountAnchor, setAccountAnchor] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const openAccount = Boolean(accountAnchor);

  const handleAccountOpen = useCallback((event) => {
    setAccountAnchor(event.currentTarget);
  }, []);

  const handleAccountClose = useCallback(() => {
    setAccountAnchor(null);
  }, []);

  const handleProfile = useCallback(() => {
    handleAccountClose();
    navigate("/profile");
  }, [navigate, handleAccountClose]);

  const handleLogout = useCallback(() => {
    handleAccountClose();
    logoutUser();
  }, [handleAccountClose]);

  const handleMobileNav = useCallback((path) => {
    setMobileDrawerOpen(false);
    if (path) navigate(path);
  }, [navigate]);

  const toggleDrawer = useCallback((open) => (event) => {
    if (
      event?.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setMobileDrawerOpen(open);
  }, []);

  const accountMenuItems = [
    {
      key: "profile",
      labelKey: "header.myAccount",
      icon: <AccountCircleIcon />,
      onClick: handleProfile,
    },
    ...(userHasGroup("Company Admin")
      ? [
          {
            key: "admin",
            labelKey: "header.adminPanel",
            icon: <AdminPanelSettingsIcon />,
            onClick: () => {
              handleAccountClose();
              navigate("/administrator/dashboard");
            },
          },
        ]
      : []),
    ...(!userHasGroup("Company Admin") &&
    userHasAnyRoleOrGroup(["AGENT_MANAGER", "Agent Manager"])
      ? [
          {
            key: "agentCabinet",
            labelKey: "header.agentCabinet",
            icon: <AttachMoneyIcon />,
            onClick: () => {
              handleAccountClose();
              navigate("/agent");
            },
          },
        ]
      : []),
    ...(!userHasGroup("Company Admin") && userHasGroup("Company Driver")
      ? [
          {
            key: "driverCabinet",
            labelKey: "header.driverCabinet",
            icon: <DirectionsBusIcon />,
            onClick: () => {
              handleAccountClose();
              navigate("/administrator/trips/passengers");
            },
          },
        ]
      : []),
    {
      key: "logout",
      labelKey: "header.logout",
      icon: <LogoutIcon />,
      onClick: handleLogout,
      divider: true,
    },
  ];

  const mobileNavContent = (
    <Box
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
      sx={{ width: 280, pt: 2, pb: 2 }}
    >
      <Box sx={{ px: 2, pb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="subtitle1" fontWeight={600} color="text.primary">
          {t("header.menu")}
        </Typography>
        <IconButton
          onClick={toggleDrawer(false)}
          aria-label={t("header.closeMenu")}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <List disablePadding>
        {MAIN_NAV.map(({ to, labelKey, icon }) => (
          <MenuItem
            key={to}
            component={Link}
            to={to}
            onClick={() => handleMobileNav(to)}
            sx={{ py: 1.5 }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: "primary.main" }}>
              {icon}
            </ListItemIcon>
            <ListItemText primary={t(labelKey)} />
          </MenuItem>
        ))}
      </List>
      <Divider sx={{ my: 1 }} />
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          {t("language.title")}
        </Typography>
        <ToggleButtonGroup
          value={i18n.language}
          exclusive
          onChange={(_, value) => value && i18n.changeLanguage(value)}
          size="small"
          fullWidth
          sx={{
            "& .MuiToggleButton-root": {
              flex: 1,
              py: 0.75,
              "&.Mui-selected": { bgcolor: "primary.main", color: "primary.contrastText" },
            },
          }}
        >
          <ToggleButton value="uk" aria-label={t("language.uk")}>UA</ToggleButton>
          <ToggleButton value="en" aria-label={t("language.en")}>EN</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Divider sx={{ my: 1 }} />
      <List disablePadding>
        {isAuthenticated() ? (
          accountMenuItems.map((item) => (
            <React.Fragment key={item.key}>
              {item.divider && <Divider sx={{ my: 1 }} />}
              <MenuItem
                onClick={() => {
                  setMobileDrawerOpen(false);
                  item.onClick();
                }}
                sx={{ py: 1.5 }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: "primary.main" }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={t(item.labelKey)} />
              </MenuItem>
            </React.Fragment>
          ))
        ) : (
          <MenuItem
            onClick={() => handleMobileNav("/login")}
            sx={{ py: 1.5 }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: "primary.main" }}>
              <LoginIcon />
            </ListItemIcon>
            <ListItemText primary={t("header.login")} />
          </MenuItem>
        )}
      </List>
    </Box>
  );

  return (
    <AppBar
      ref={headerRef}
      position="fixed"
      component="header"
      sx={{
        backgroundColor: BRAND.primary,
        boxShadow: theme.shadows[2],
        borderRadius: 0
      }}
      aria-label={t("header.mainNav")}
    >
      <Container>
        <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {/* Ліва частина: логотип + Про нас + Контакти */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button
              color="inherit"
              component={Link}
              to="/"
              onClick={(event) => scrollLandingToTop(event, location.pathname)}
              sx={{ p: 0, minWidth: 0 }}
              aria-label={t("header.toHome")}
            >
              {brand.logoSrc ? (
                <Box
                  component="img"
                  src={brand.logoSrc}
                  alt={brand.name}
                  sx={{ height: 60 }}
                  loading="eager"
                />
              ) : (
                <Typography variant="h6" fontWeight={700} color="inherit" sx={{ px: 1 }}>
                  {brand.name}
                </Typography>
              )}
            </Button>

            {!isMobile && (
              <Box
                component="nav"
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                aria-label={t("header.mainMenu")}
              >
                <Button
                  color="inherit"
                  component={Link}
                  to="/about"
                  sx={{ fontWeight: 500 }}
                >
                  {t("header.about")}
                </Button>
                <Button
                  color="inherit"
                  component={Link}
                  to="/contact"
                  sx={{ fontWeight: 500 }}
                >
                  {t("header.contacts")}
                </Button>
              </Box>
            )}
          </Box>

          {/* Права частина: перемикач мови + логін/ім'я користувача або кнопка меню */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {!isMobile && (
              <>
                <ToggleButtonGroup
                  value={i18n.language}
                  exclusive
                  onChange={(_, value) => value && i18n.changeLanguage(value)}
                  size="small"
                  sx={{
                    "& .MuiToggleButton-root": {
                      color: "rgba(255,255,255,0.9)",
                      borderColor: "rgba(255,255,255,0.5)",
                      py: 0.25,
                      px: 1,
                      "&.Mui-selected": { bgcolor: "rgba(255,255,255,0.2)", color: "white" },
                      "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
                    },
                  }}
                >
                  <ToggleButton value="uk" aria-label={t("language.uk")}>UA</ToggleButton>
                  <ToggleButton value="en" aria-label={t("language.en")}>EN</ToggleButton>
                </ToggleButtonGroup>

                {isAuthenticated() ? (
                  <>
                    <Button
                      color="inherit"
                      onClick={handleAccountOpen}
                      startIcon={<AccountCircleIcon />}
                      aria-haspopup="menu"
                      aria-expanded={openAccount}
                      aria-controls={openAccount ? "account-menu" : undefined}
                      id="account-button"
                      sx={{ fontWeight: 500 }}
                    >
                      <Typography variant="body2" component="span" noWrap sx={{ maxWidth: 160 }}>
                        {email || t("header.account")}
                      </Typography>
                    </Button>
                    <Menu
                      id="account-menu"
                      anchorEl={accountAnchor}
                      open={openAccount}
                      onClose={handleAccountClose}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                      transitionDuration={{ enter: 200, exit: 150 }}
                      MenuListProps={{
                        "aria-labelledby": "account-button",
                        sx: { minWidth: 220, py: 0.5 },
                      }}
                      slotProps={{
                        paper: {
                          elevation: 8,
                          sx: { mt: 1.5, overflow: "visible" },
                        },
                      }}
                    >
                      {accountMenuItems.map((item) => (
                        <React.Fragment key={item.key}>
                          {item.divider && <Divider sx={{ my: 1 }} />}
                          <MenuItem onClick={item.onClick} sx={{ py: 1.25 }}>
                            <ListItemIcon sx={{ minWidth: 40, color: "primary.main" }}>
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText primary={t(item.labelKey)} />
                          </MenuItem>
                        </React.Fragment>
                      ))}
                    </Menu>
                  </>
                ) : (
                  <Button
                    color="inherit"
                    variant="outlined"
                    component={Link}
                    to="/login"
                    startIcon={<LoginIcon />}
                    sx={{
                      borderColor: "rgba(255,255,255,0.5)",
                      "&:hover": { borderColor: "white", bgcolor: "rgba(255,255,255,0.08)" },
                    }}
                  >
                    {t("header.login")}
                  </Button>
                )}
              </>
            )}

            {isMobile && (
              <IconButton
                color="inherit"
                onClick={toggleDrawer(true)}
                aria-label={t("header.openMenu")}
                aria-expanded={mobileDrawerOpen}
                size="large"
              >
                <MenuIcon fontSize="medium" />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </Container>

      <Drawer
        anchor="right"
        open={mobileDrawerOpen}
        onClose={toggleDrawer(false)}
        slotProps={{
          backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.4)" } },
        }}
        PaperProps={{
          sx: {
            boxShadow: theme.shadows[16],
          },
        }}
        aria-label={t("header.mobileMenu")}
      >
        {mobileNavContent}
      </Drawer>
    </AppBar>
  );
};

export default Header;
